import { useMemo } from "react";

import type { AttributeReviewValueFixInput } from "@/graphql/generated";
import type { AttributeReviewDraftSelection } from "@/hooks/useAttributeReview.types";

export function useRowDecision(
  draft: AttributeReviewDraftSelection | undefined,
  onChange: (next: AttributeReviewDraftSelection | null) => void,
) {
  const kind = draft?.kind ?? null;
  const leaf = draft?.leaf ?? null;
  const path = draft?.path ?? null;
  const fixList = useMemo(() => draft?.values ?? [], [draft?.values]);
  const fixes = useMemo(
    () => Object.fromEntries(fixList.map((fix) => [fix.storeValue, fix])),
    [fixList],
  );
  const save = (next: AttributeReviewDraftSelection) =>
    onChange(
      next.kind || next.leaf || next.path || next.pool || next.values?.length
        ? { ...next, values: next.values?.length ? next.values : undefined }
        : null,
    );
  return {
    kind,
    setKind: (next: { kind: string }) =>
      save({ kind: next.kind, path: draft?.path }),
    leaf,
    path,
    setPath: (next: string | null, nextKind?: string | null) =>
      save({
        kind: nextKind ?? kind ?? undefined,
        leaf: undefined,
        path: next,
        values: fixList,
      }),
    pool: draft?.pool ?? null,
    setPool: (pool: string) =>
      save({
        kind: kind ?? undefined,
        leaf: leaf ?? undefined,
        path: draft?.path,
        pool,
      }),
    fixes,
    setFix: (fix: AttributeReviewValueFixInput) =>
      save({
        kind: kind ?? undefined,
        leaf: leaf ?? undefined,
        path: draft?.path,
        values: [
          ...fixList.filter((f) => f.storeValue !== fix.storeValue),
          fix,
        ],
      }),
    undoFix: (storeValue: string) =>
      save({
        kind: kind ?? undefined,
        leaf: leaf ?? undefined,
        path: draft?.path,
        values: fixList.filter((f) => f.storeValue !== storeValue),
      }),
  };
}
